/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {
    ElementPosition,
    getSetting,
    IndexedObject,
    PDF_VARIABLES,
    PDFElementParams,
    PDFElementPersist,
    PDFElementSubType,
    PDFElementType,
    PDFTextVariables
} from '@dvrd/dvr-controls';

import Account from '../models/accountModel';

import PDFTemplate from '../models/pdfTemplateModel';

import i18n from '../i18n';

export const getCustomVariables = (): PDFTextVariables => ({
    company: {...PDF_VARIABLES.company},
    client: {
        ...PDF_VARIABLES.client,
        klant_voornaam: i18n.t('util.pdf.var.firstName', 'Voornaam'),
        klant_achternaam: i18n.t('util.pdf.var.lastName', 'Achternaam'),
        aflever_adres: i18n.t('util.pdf.var.deliveryAddress', 'Afleveradres'),
        aflever_postcode: i18n.t('util.pdf.var.deliveryZip', 'Afleverpostcode'),
        aflever_stad: i18n.t('util.pdf.var.deliveryCity', 'Afleverstad'),
        aflever_land: i18n.t('util.pdf.var.deliveryCountry', 'Afleverland'),
        aflever_extra: i18n.t('util.pdf.var.deliveryExtra', 'Afleveradres toevoeging')
    },
    invoice: {
        ...PDF_VARIABLES.invoice,
        order_id: i18n.t('util.pdf.var.orderId', 'Ordernummer'),
        order_datum: i18n.t('util.pdf.var.orderDate', 'Orderdatum'),
        excl_btw_bedragen: i18n.t('util.pdf.var.amountsExclVat', 'Bedragen excl. BTW')
    }
});

export const createDefaultValues = (account: Account | null): IndexedObject<string> => {
    if (account) {
        const hasActiveKey = account.hasActiveAPIKey();
        return {
            bedrijf_naam: account.companyName,
            bedrijf_adres: account.address,
            bedrijf_postcode: account.zipCode,
            bedrijf_stad: account.city,
            bedrijf_land: account.country,
            bedrijf_kvk: account.cocNumber.length
                ? account.cocNumber
                : hasActiveKey
                  ? i18n.t('util.pdf.notSet', 'niet ingesteld')
                  : i18n.t('util.pdf.noBolConnection', 'geen Bol koppeling'),
            bedrijf_btw: account.vatNumber.length
                ? account.vatNumber
                : hasActiveKey
                  ? i18n.t('util.pdf.notSet', 'niet ingesteld')
                  : i18n.t('util.pdf.noBolConnection', 'geen Bol koppeling'),
            bedrijf_email: account.settings.replyEmail,
            bedrijf_telefoon: account.telNumber,
            bedrijf_iban: account.iban || ''
        };
    }
    return {};
};

export const templateToItems = (template: PDFTemplate): PDFElementParams<any, any>[] => {
    const {elements} = template,
        items: PDFElementParams<any, any>[] = [];
    for (const element of elements) {
        const dimensions = {
                width: element.width,
                height: element.height,
                left: element.left,
                top: element.top
            },
            key: string = element.id,
            type = element.elementType,
            {options, persistent} = element;

        // Skip image elements without an image
        if (type === PDFElementType.IMAGE) {
            if (!options.img) continue;
            const filePrefix = getSetting('filePrefix');
            if (!options.img.startsWith(filePrefix)) options.img = `${getSetting('filePrefix')}${options.img}`;
        }

        items.push({dimensions, key, type, options: {...options}, persistent, linkedID: element.linkedId || undefined});
    }
    return items;
};

export const getDefaultPDFItems = (account: Account | null, image?: string) =>
    accountElements(account).concat(imgElement(image).concat(DefaultPDFItems.slice()));

export const prepareForSubmit = (items: PDFElementParams<any, any>[]): PDFElementParams<any, any>[] => {
    const prefix = getSetting('filePrefix');
    for (const item of items)
        if (item.type === PDFElementType.IMAGE && typeof item.options.img === 'string')
            item.options.img = item.options.img.replace(prefix, '');
    return items;
};

const accountElements = (account: Account | null): PDFElementParams<any, any>[] => {
    const items: PDFElementParams<any, any>[] = [],
        bol = !account || account.settings.useBolText;
    if (bol) {
        items.push({
            dimensions: {left: 60, top: 640, width: 149, height: 24},
            key: 'a76b7be6-93d7-406f-b83f-ec6ba860d37c',
            type: 'text',
            options: {
                fontSize: 11,
                bold: true,
                italic: false,
                text: 'Betaling verloopt via Bol',
                underline: false,
                font: 'lato',
                color: '#000'
            },
            persistent: PDFElementPersist.CONFIRM
        });
    }
    return items;
};
const imgElement = (image?: string): Array<PDFElementParams<any, any>> => {
    if (!image) return [];

    const prefix: string = getSetting('filePrefix');
    if (!image.startsWith(prefix)) image = prefix + image;

    return [
        {
            key: '43ecabe3-aaca-4c86-a176-cd09754ff174',
            dimensions: {left: 60, top: 70, width: 113, height: 28},
            type: 'image',
            options: {alignment: 'left', img: image},
            persistent: PDFElementPersist.NOT_PERSISTENT
        }
    ];
};

const DefaultPDFItems: PDFElementParams<any, any>[] = [
    {
        dimensions: {left: 60, top: 210, width: 73, height: 38},
        key: '07d85e05-3576-411c-9381-55af3530dac3',
        type: 'text',
        sub_type: PDFElementSubType.INVOICE_TITLE,
        options: {
            fontSize: 19,
            bold: true,
            italic: false,
            text: 'Factuur',
            underline: false,
            font: 'lato',
            color: '#000'
        }
    },
    {
        dimensions: {left: 60, top: 270, width: 115, height: 75},
        key: 'c0582f17-8eb2-430c-a702-246ecf6bc644',
        type: 'text',
        options: {
            fontSize: 9,
            bold: false,
            italic: false,
            text: '{klant_bedrijf}\n{klant_naam}\n{klant_adres}\n{klant_postcode} {klant_stad}\n{klant_land}\n{klant_extra}\nBTW nr: {klant_btw}',
            underline: false,
            font: 'lato',
            color: '#000'
        }
    },
    {
        dimensions: {left: 60, top: 430, width: 471, height: 200},
        type: 'invoice_table',
        key: 'ee44b6d9-8142-436f-a44a-4280184c41c1',
        options: {
            widths: [40, 20, 10, 10, 20],
            fontSize: 9,
            font: 'lato',
            color: '#000',
            alignment: ElementPosition.CENTER
        }
    },
    {
        dimensions: {left: 350, top: 70, width: 178, height: 29},
        key: '6fae2691-2652-4dc0-981b-d3745bdf4b07',
        type: 'text',
        options: {
            fontSize: 14,
            bold: false,
            italic: false,
            text: '{bedrijf_naam}',
            underline: false,
            font: 'lato',
            color: '#000'
        },
        linkedID: 'company-texts'
    },
    {
        dimensions: {left: 350, top: 100, width: 103, height: 80},
        key: '6475ee92-50a6-4778-a016-908b80792826',
        type: 'text',
        options: {
            fontSize: 8,
            bold: false,
            italic: false,
            text: '{bedrijf_adres}\n{bedrijf_postcode} {bedrijf_stad}\n{bedrijf_land}\nKvK nr: {bedrijf_kvk}\nBTW nr: {bedrijf_btw}',
            underline: false,
            font: 'lato',
            color: '#000'
        },
        linkedID: 'company-texts'
    },
    {
        dimensions: {left: 360, top: 270, width: 82, height: 48},
        key: '0d8ece0d-32e4-46e6-8912-e51ae8a0a289',
        type: 'text',
        options: {
            fontSize: 9,
            bold: false,
            italic: false,
            text: 'Factuurdatum:' + '\n' + 'Order id bij Bol:' + '\n' + 'Factuurnummer:',
            underline: false,
            font: 'lato',
            color: '#a9a9a9'
        }
    },
    {
        dimensions: {left: 470, top: 270, width: 70, height: 48},
        key: '0eb4f114-632c-494a-bfed-8744e142c0e5',
        type: 'text',
        options: {
            fontSize: 9,
            bold: false,
            italic: false,
            text: '{factuurdatum}\n{order_id}\n{factuurnummer}',
            underline: false,
            font: 'lato',
            color: '#000'
        }
    }
];
